/**
 * Shared free gift rules.
 *
 * One place decides how many of each gift belongs in the cart. The cart drawer,
 * the cart page and the product page block all read from here, so they cannot
 * drift apart the way three separate copies of this logic did.
 *
 * @typedef {{variantId: string, threshold: number}} GiftRule
 * @typedef {Record<string, [number, number]>} DeviceWeights product id to [devices, gift devices]
 * @typedef {{mainProductId: string, gifts: GiftRule[], doubleThreshold: number, weights: DeviceWeights}} GiftConfig
 */

/**
 * Reads the configuration Liquid rendered onto an element's data attributes.
 *
 * @param {HTMLElement | null | undefined} element
 * @returns {GiftConfig | null} null when the element carries no usable config
 */
export function readGiftConfig(element) {
  const mainProductId = element?.dataset.mainProductId || '';
  if (!mainProductId) return null;

  const gifts = [1, 2, 3]
    .map((index) => ({
      variantId: element?.dataset[`gift${index}Id`] || '',
      threshold: parseInt(element?.dataset[`gift${index}Threshold`] || '', 10),
    }))
    .filter((gift) => gift.variantId !== '' && gift.threshold > 0);

  if (gifts.length === 0) return null;

  /** @type {DeviceWeights} */
  let weights = {};
  try {
    weights = JSON.parse(element?.dataset.deviceWeights || '{}') || {};
  } catch (error) {
    weights = {};
  }

  return {
    mainProductId,
    gifts,
    doubleThreshold: parseInt(element?.dataset.giftDoubleThreshold || '', 10) || 0,
    weights,
  };
}

/**
 * How many units of the main product the cart holds.
 *
 * This matches on product id rather than on a handle substring. The old
 * substring match counted any product whose handle happened to contain the
 * configured text, which meant a gift named after the main product could push
 * the cart over its own threshold, and renaming a handle switched the whole
 * system off without a word.
 *
 * @param {any} cart
 * @param {string} mainProductId
 * @returns {number}
 */
export function countMainUnits(cart, mainProductId) {
  if (!cart || !Array.isArray(cart.items)) return 0;

  return cart.items.reduce(
    (/** @type {number} */ total, /** @type {any} */ item) =>
      String(item.product_id) === String(mainProductId) ? total + item.quantity : total,
    0
  );
}

/**
 * How many devices in the cart count toward the free gifts: every unit of the
 * main product, plus the gift devices of other products (bundles), from
 * their cart_devices and cart_gifts_included metafields. A product missing
 * from the weights counts nothing.
 *
 * @param {any} cart
 * @param {GiftConfig} config
 * @param {DeviceWeights} [weights] defaults to the weights in the config
 * @returns {number}
 */
export function countGiftUnits(cart, config, weights = config.weights) {
  if (!cart || !Array.isArray(cart.items)) return 0;

  return cart.items.reduce((/** @type {number} */ total, /** @type {any} */ item) => {
    if (String(item.product_id) === String(config.mainProductId)) return total + item.quantity;
    const giftDevices = weights?.[String(item.product_id)]?.[1] || 0;
    return total + item.quantity * giftDevices;
  }, 0);
}

/**
 * The line updates that would bring the cart's gifts in line with the rules.
 * An empty object means the cart is already correct and nothing should be sent.
 *
 * @param {any} cart
 * @param {GiftConfig | null} config
 * @param {DeviceWeights} [weights] defaults to the weights in the config
 * @returns {Record<string, number>}
 */
export function resolveGiftUpdates(cart, config, weights) {
  /** @type {Record<string, number>} */
  const updates = {};
  if (!cart || !Array.isArray(cart.items) || !config) return updates;

  const mainUnits = countGiftUnits(cart, config, weights || config.weights);
  const doubled = config.doubleThreshold > 0 && mainUnits >= config.doubleThreshold;

  for (const gift of config.gifts) {
    const target = mainUnits >= gift.threshold ? (doubled ? 2 : 1) : 0;
    const lines = cart.items.filter((/** @type {any} */ item) => String(item.variant_id) === String(gift.variantId));

    if (lines.length === 0) {
      // Adding by variant id rather than line key, because there is no line yet.
      if (target > 0) updates[gift.variantId] = target;
      continue;
    }

    if (lines[0].quantity !== target) updates[lines[0].key] = target;

    // A gift can land on more than one line when two places add it at once.
    for (let index = 1; index < lines.length; index += 1) updates[lines[index].key] = 0;
  }

  return updates;
}

/**
 * Every quantity at which the set of gifts changes. Used to tell the customer
 * something is on its way before the cart has finished updating.
 *
 * @param {GiftConfig | null} config
 * @returns {number[]}
 */
export function giftThresholds(config) {
  if (!config) return [];

  const thresholds = config.gifts.map((gift) => gift.threshold);
  if (config.doubleThreshold > 0) thresholds.push(config.doubleThreshold);

  return thresholds;
}
